/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { AppService } from '@services/app/app.service';
import { ERRORS } from '@models/app/errors';
import { Injectable } from '@angular/core';
import { Params, Route, Router, UrlTree } from '@angular/router';
import { ROUTES_TREE } from '@core/config/routes-tree';
import { RouteCodes } from '@core/config/route-codes';
import { Location } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class NavigatorService {
  /**
   * path actions impostati
   */
  private pathActions: { [code: string]: Params } = {};

  constructor(
    private router: Router,
    private location: Location,
    private appService: AppService
  ) {}

  /**
   * determina se la rotta è abilitata
   */
  isRouteEnabled(route: Route): boolean {
    const code = this.getRouteCodeFromRoute(route);

    // se non è definito il route code considero la pagina come abilitata
    if (typeof code !== 'string') {
      return true;
    }

    // altrimenti verifico se è attivo nella configurazione
    return this.isRouteCodeActive(code);
  }

  /**
   * restituisce la stringa di redirect associata alla route se configurata
   */
  getRouteRedirectPath(route: Route): string {
    const code = this.getRouteCodeFromRoute(route);

    if (typeof code !== 'string') {
      return null;
    }

    const activation = this.appService.getRoutesActivation()[code];

    // se activation è una stringa vuol dire che è impostato un redirect
    if (typeof activation === 'string') {
      return activation;
    } else {
      return null;
    }
  }

  /**
   * naviga verso l'entry principale
   */
  navigateToMain(): Promise<boolean> {
    if (this.appService.isStandalone()) {
      return this.navigateToReservedArea();
    } else {
      return this.navigateToPosition();
    }
  }

  /**
   * naviga verso l'area riservata
   */
  navigateToReservedArea(): Promise<boolean> {
    return this.navigateToMainRoute(RouteCodes.reservedArea);
  }

  /**
   * naviga verso l'area dedicata alle utenze anonime (non clienti)
   */
  navigateToAnonymousArea(): Promise<boolean> {
    return this.navigateToMainRoute(RouteCodes.anonymous);
  }

  /**
   * naviga verso la position
   */
  navigateToPosition(): Promise<boolean> {
    return this.navigateToMainRoute(RouteCodes.position);
  }

  /**
   * naviga verso la pagina di logon
   */
  navigateToLogon(): Promise<boolean> {
    return this.navigateToMainRoute(RouteCodes.logon);
  }

  /**
   * naviga verso la pagina di registrazione
   */
  navigateToRegister(): Promise<boolean> {
    return this.navigateToMainRoute(RouteCodes.register);
  }

  /**
   * naviga verso la pagina "finta"
   */
  navigateToDummy(): Promise<boolean> {
    return this.navigateToMainRoute(RouteCodes.dummy);
  }

  /**
   * naviga verso di conclusione
   */
  navigateToExit(reason?: string): Promise<boolean> {
    return this.navigateToUrl(
      this.createMainRouteUrlTree(
        RouteCodes.exit,
        reason ? { reason } : undefined
      )
    );
  }

  /**
   * naviga verso la pagina di errore
   */
  navigateToError(code: ERRORS): Promise<boolean> {
    return this.navigateToUrl(this.createErrorUrlTree(code));
  }

  /**
   * naviga indietro
   */
  navigateBack(): void {
    return this.location.back();
  }

  /**
   * naviga verso la rotta principale corrispondente al codice
   */
  navigateToMainRoute(code: RouteCodes): Promise<boolean> {
    const urlTree = this.createMainRouteUrlTree(code);

    if (!(urlTree instanceof UrlTree)) {
      return Promise.reject(`Path for ${code} doesn't exist`);
    }

    return this.navigateToUrl(urlTree);
  }

  /**
   * naviga verso la rotta child corrispondente al codice
   */
  navigateToChildRoute(
    code: RouteCodes,
    childCode: RouteCodes
  ): Promise<boolean> {
    const urlTree = this.createChildRouteUrlTree(code, childCode);

    if (!(urlTree instanceof UrlTree)) {
      return Promise.reject(`Path for ${code}-${childCode} doesn't exist`);
    }

    return this.navigateToUrl(urlTree);
  }

  /**
   * naviga verso l'url richiesto
   */
  navigateToUrl(url: string | UrlTree): Promise<boolean> {
    return this.router.navigateByUrl(url);
  }

  /**
   * crea l'url tree per la pagina di errore
   */
  createErrorUrlTree(code?: ERRORS): UrlTree {
    return this.createMainRouteUrlTree(RouteCodes.error, {
      code
    });
  }

  /**
   * costruisce l'url tree di una rotta principale
   */
  createMainRouteUrlTree(code: RouteCodes, queryParams?: Params): UrlTree {
    const path = this.getMainRoutePath(code);

    if (!path) {
      return;
    }

    return this.createUrlTree(`/${path}`, queryParams);
  }

  /**
   * costruisce l'url tree di una rotta child
   */
  createChildRouteUrlTree(
    code: RouteCodes,
    childCode: RouteCodes,
    queryParams?: Params
  ): UrlTree {
    const mainPath = this.getMainRoutePath(code);
    const childPath = this.getChildRoutePath(code, childCode);

    if (!mainPath || !childPath) {
      return;
    }

    return this.createUrlTree(`/${mainPath}/${childPath}`, queryParams);
  }

  /**
   * crea l'url tree per il path desiderato
   */
  createUrlTree(path: string, queryParams?: Params): UrlTree {
    return this.router.createUrlTree([path], {
      queryParams: queryParams ? { ...queryParams } : undefined
    });
  }

  /**
   * aggiunge delle azioni definite come params
   */
  addPathActions(code: string, actions: Params): void {
    if (!!code && !!actions && Object.keys(actions).length > 0) {
      this.pathActions[code] = { ...actions };
    }
  }

  /**
   * restituisce le azioni definite per il codice e
   * le elimina
   */
  usePathActions(codes: string[]): Params {
    let actions = {};

    codes.forEach(code => {
      if (!this.pathActions[code]) {
        return;
      }

      actions = Object.assign(actions, this.pathActions[code]);
      delete this.pathActions[code];
    });

    return actions;
  }

  /**
   * ricarica l'app partendo dalla pagina di login
   */
  reloadFromStart(): void {
    this.navigateToLogon().then(() => location.reload());
  }

  /**
   * restituisce il path corrispondente al codice
   */
  private getMainRoutePath(code: RouteCodes): string {
    return ROUTES_TREE[code] && ROUTES_TREE[code].path;
  }

  /**
   * restituisce il path della rotta child corrispondente al codice
   */
  private getChildRoutePath(code: RouteCodes, childCode: RouteCodes): string {
    return (
      ROUTES_TREE[code] &&
      ROUTES_TREE[code].children &&
      ROUTES_TREE[code].children[childCode] &&
      ROUTES_TREE[code].children[childCode].path
    );
  }

  /**
   * determina se il route code è impostato come attivo nell'oggetto di configurazione;
   * per essere considerato come attivo il suo valore può essere true o una stringa valida
   */
  private isRouteCodeActive(code: string): boolean {
    const activation = this.appService.getRoutesActivation()[code];

    return (
      activation === true || (typeof activation === 'string' && !!activation)
    );
  }

  /**
   * estrae il route code dall'oggetto Route
   */
  private getRouteCodeFromRoute(route: Route): string {
    return (route && route.data && route.data.routeCode) || undefined;
  }
}
